import { useState, useEffect } from 'react';
import StatusDot from '../components/StatusDot';
import { authFetch, getSession, getBuilderAnalytics } from '../api';

const s = {
  h1: { fontSize: 26, fontWeight: 600, marginBottom: 4 },
  sub: { color: 'var(--muted)', marginBottom: 32 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 32 },
  card: { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20 },
  cardLabel: { fontSize: 12, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 },
  cardVal: { fontSize: 28, fontWeight: 600, color: 'var(--accent2)' },
  cardSub: { fontSize: 12, color: 'var(--muted)', marginTop: 4 },
  section: { marginBottom: 32 },
  sectionTitle: { fontSize: 14, fontWeight: 600, marginBottom: 14 },
  buildCard: { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 14 },
  buildRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  buildName: { fontSize: 16, fontWeight: 600, marginBottom: 4 },
  buildSlug: { fontSize: 12, color: 'var(--muted)', fontFamily: 'monospace' },
  buildStats: { display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' },
  stat: { textAlign: 'center' },
  statVal: { fontWeight: 600, fontSize: 15, color: 'var(--accent2)' },
  statLabel: { fontSize: 11, color: 'var(--muted)', marginTop: 2 },
  toggleBtn: (live) => ({
    padding: '7px 16px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500,
    border: `1px solid ${live ? 'var(--red)' : 'var(--green)'}`,
    background: 'transparent',
    color: live ? 'var(--red)' : 'var(--green)',
    cursor: 'pointer',
  }),
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--muted)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' },
  td: { padding: '8px 12px', borderBottom: '1px solid var(--border)' },
  badge: (ok) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 500,
    background: ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
    color: ok ? 'var(--green)' : 'var(--red)',
  }),
  stripeBox: (variant) => ({
    background: 'var(--bg2)',
    border: `1px solid ${variant === 'ok' ? 'rgba(34,197,94,0.3)' : variant === 'warn' ? 'rgba(234,179,8,0.3)' : 'var(--border)'}`,
    borderRadius: 'var(--radius)', padding: 20, marginBottom: 24,
  }),
  stripeBtn: { padding: '9px 20px', borderRadius: 'var(--radius-sm)', border: 'none', background: '#635BFF', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' },
  emptyTitle: { fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 8 },
  emptyBtn: { padding: '10px 24px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginTop: 16 },
};

export default function Dashboard({ navigate }) {
  const session = getSession();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [toggling, setToggling]   = useState({});
  const [stripeStatus, setStripeStatus] = useState(null);
  const [toolsPanel, setToolsPanel]     = useState(null); // slug of build showing tools
  const [buildTools, setBuildTools]      = useState({});   // { slug: [tools] }
  const [newTool, setNewTool]           = useState({ name: '', slug: '', description: '', command: '', pricePerCall: '0.10', category: 'default' });
  const [toolError, setToolError]       = useState('');
  const [scanResults, setScanResults]   = useState({});   // { slug: scanData }
  const [scanning, setScanning]         = useState({});   // { slug: true }
  const [toolSaving, setToolSaving]     = useState(false);
  const [jobTypesPanel, setJobTypesPanel] = useState(null);
  const [buildJobTypes, setBuildJobTypes] = useState({});
  const [newJobType, setNewJobType] = useState({ name: '', slug: '', description: '', price: '1.00', timeoutMs: '300000', category: 'default' });
  const [jobTypeError, setJobTypeError] = useState('');
  const [jobTypeSaving, setJobTypeSaving] = useState(false);
  const [analyticsPanel, setAnalyticsPanel] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({});
  const [analyticsLoading, setAnalyticsLoading] = useState({});
  const [verifyPanel, setVerifyPanel] = useState(null); // slug showing verification
  const [verifyData, setVerifyData] = useState({});
  const [verifyLoading, setVerifyLoading] = useState({});
  const [verifyForm, setVerifyForm] = useState({ modelsUsed: '', dataSources: '', dataFreshness: 'real-time', privacyStance: 'no_logging', serverRegion: '' });
  const [verifySubmitting, setVerifySubmitting] = useState(false);

  useEffect(() => {
    if (session) loadDashboard();
    // eslint-disable-next-line
  }, []);

  async function loadDashboard() {
    setLoading(true); setError('');
    try {
      const dashRes = await authFetch('/v1/dashboard/me');
      const dash = await dashRes.json();
      if (!dashRes.ok) { setError(dash.error || 'Failed to load'); setLoading(false); return; }

      // Load stripe status using creatorId from dashboard response
      if (dash.creatorId) {
        try {
          const stripeRes = await authFetch(`/v1/billing/connect/${dash.creatorId}`);
          setStripeStatus(await stripeRes.json());
        } catch {}
      }

      setData(dash);
    } catch {
      setError('Could not load dashboard');
    }
    setLoading(false);
  }

  async function runScan(slug) {
    setScanning(s => ({ ...s, [slug]: true }));
    setScanResults(r => ({ ...r, [slug]: null }));
    try {
      const r = await authFetch(`/v1/builder/builds/${slug}/scan`, { method: 'POST' });
      const d = await r.json();
      setScanResults(prev => ({ ...prev, [slug]: d }));
    } catch {
      setScanResults(prev => ({ ...prev, [slug]: { passed: false, summary: 'Scan failed — server error', issues: [], tools: { total: 0, passed: 0, failed: 0, results: [] } } }));
    }
    setScanning(s => ({ ...s, [slug]: false }));
  }

  async function toggleBuild(slug) {
    if (!data?.creatorId) return;
    // If going LIVE, run scan first
    const build = data.builds.find(b => b.slug === slug);
    if (build && !build.is_live) {
      setToggling(t => ({ ...t, [slug]: true }));
      try {
        const scanRes = await authFetch(`/v1/builder/builds/${slug}/scan`, { method: 'POST' });
        const scan = await scanRes.json();
        setScanResults(prev => ({ ...prev, [slug]: scan }));
        if (!scan.passed) {
          setToggling(t => ({ ...t, [slug]: false }));
          return; // Block — critical issues found
        }
      } catch {}
    }
    setToggling(t => ({ ...t, [slug]: true }));
    try {
      const r = await authFetch(`/v1/builds/${slug}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creatorId: data.creatorId }),
      });
      const d = await r.json();
      setData(prev => ({
        ...prev,
        builds: prev.builds.map(b => b.slug === slug ? { ...b, is_live: d.is_live } : b),
      }));
    } catch {}
    setToggling(t => ({ ...t, [slug]: false }));
  }

  async function loadAnalytics(slug) {
    if (analyticsPanel === slug) { setAnalyticsPanel(null); return; }
    setAnalyticsPanel(slug);
    if (analyticsData[slug]) return; // already loaded
    setAnalyticsLoading(l => ({ ...l, [slug]: true }));
    try {
      const data = await getBuilderAnalytics(slug, 30);
      setAnalyticsData(prev => ({ ...prev, [slug]: data }));
    } catch {}
    setAnalyticsLoading(l => ({ ...l, [slug]: false }));
  }

  async function loadVerification(slug) {
    if (verifyPanel === slug) { setVerifyPanel(null); return; }
    setVerifyPanel(slug);
    if (verifyData[slug]) return;
    setVerifyLoading(l => ({ ...l, [slug]: true }));
    try {
      const res = await fetch(`/v1/builds/${slug}/verification`);
      const data = await res.json();
      setVerifyData(prev => ({ ...prev, [slug]: data }));
      if (data.tier2?.models_used) {
        setVerifyForm({ modelsUsed: data.tier2.models_used || '', dataSources: data.tier2.data_sources || '', dataFreshness: data.tier2.data_freshness || 'real-time', privacyStance: data.tier2.privacy_stance || 'no_logging', serverRegion: data.tier2.server_region || '' });
      }
    } catch {}
    setVerifyLoading(l => ({ ...l, [slug]: false }));
  }

  async function submitVerification(slug) {
    setVerifySubmitting(true);
    try {
      const res = await authFetch(`/v1/builder/builds/${slug}/verification`, {
        method: 'POST', body: JSON.stringify(verifyForm),
      });
      const data = await res.json();
      setVerifyData(prev => ({ ...prev, [slug]: { ...prev[slug], ...data } }));
      // Reload verification data
      const fresh = await fetch(`/v1/builds/${slug}/verification`);
      const freshData = await fresh.json();
      setVerifyData(prev => ({ ...prev, [slug]: freshData }));
    } catch {}
    setVerifySubmitting(false);
  }

  async function loadTools(slug) {
    try {
      const r = await fetch(`/v1/builds/${slug}/tools`);
      const d = await r.json();
      setBuildTools(prev => ({ ...prev, [slug]: d.tools || [] }));
    } catch {}
  }

  async function addTool(slug) {
    setToolError(''); setToolSaving(true);
    try {
      const r = await authFetch(`/v1/builds/${slug}/tools`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTool),
      });
      const d = await r.json();
      if (d.error) { setToolError(d.error); }
      else {
        setNewTool({ name: '', slug: '', description: '', command: '', pricePerCall: '0.10', category: 'default' });
        loadTools(slug);
      }
    } catch { setToolError('Failed to add tool'); }
    setToolSaving(false);
  }

  async function deleteTool(buildSlug, toolSlug) {
    if (!window.confirm(`Delete tool "${toolSlug}"? This cannot be undone.`)) return;
    try {
      await authFetch(`/v1/builds/${buildSlug}/tools/${toolSlug}`, { method: 'DELETE' });
      loadTools(buildSlug);
    } catch {}
  }

  function openToolsPanel(slug) {
    if (toolsPanel === slug) { setToolsPanel(null); return; }
    setToolsPanel(slug);
    setToolError('');
    if (!buildTools[slug]) loadTools(slug);
  }

  function autoToolSlug(name) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  // ─── Job Type Management ──────────────────────────────────────────────
  async function loadJobTypes(slug) {
    try {
      const r = await authFetch(`/v1/builds/${slug}/job-types`);
      const d = await r.json();
      setBuildJobTypes(prev => ({ ...prev, [slug]: Array.isArray(d) ? d : [] }));
    } catch {}
  }

  function openJobTypesPanel(slug) {
    if (jobTypesPanel === slug) { setJobTypesPanel(null); return; }
    setJobTypesPanel(slug);
    setJobTypeError('');
    if (!buildJobTypes[slug]) loadJobTypes(slug);
  }

  async function addJobType(slug) {
    setJobTypeError(''); setJobTypeSaving(true);
    try {
      const r = await authFetch(`/v1/builder/builds/${slug}/job-types`, {
        method: 'POST',
        body: JSON.stringify({
          name: newJobType.name, slug: newJobType.slug,
          description: newJobType.description, price: parseFloat(newJobType.price),
          timeoutMs: parseInt(newJobType.timeoutMs), category: newJobType.category,
        }),
      });
      const d = await r.json();
      if (d.error) setJobTypeError(d.error);
      else {
        setNewJobType({ name: '', slug: '', description: '', price: '1.00', timeoutMs: '300000', category: 'default' });
        loadJobTypes(slug);
      }
    } catch { setJobTypeError('Failed to add job type'); }
    setJobTypeSaving(false);
  }

  async function deleteJobType(buildSlug, typeSlug) {
    if (!window.confirm(`Delete job type "${typeSlug}"? This cannot be undone.`)) return;
    try {
      await authFetch(`/v1/builder/builds/${buildSlug}/job-types/${typeSlug}`, { method: 'DELETE' });
      loadJobTypes(buildSlug);
    } catch {}
  }

  async function connectStripe() {
    if (!data?.creatorId) return;
    try {
      const r = await fetch('/v1/billing/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creatorId: data.creatorId }),
      });
      const d = await r.json();
      if (d.url && (d.url.startsWith('https://connect.stripe.com') || d.url.startsWith('https://checkout.stripe.com'))) window.location.href = d.url;
      else setError(d.error || 'Stripe connect failed');
    } catch {
      setError('Stripe connect failed');
    }
  }

  // ── Not signed in ─────────────────────────────────────────────────────────
  if (!session) return (
    <div style={s.emptyState}>
      <div style={s.emptyTitle}>Sign in to view your creator dashboard</div>
      <div>Your builds, earnings, and analytics in one place.</div>
      <button style={s.emptyBtn} onClick={() => navigate('signin')}>Sign in</button>
    </div>
  );

  if (loading) return <div style={{ color: 'var(--muted)', padding: 40 }}>Loading...</div>;
  if (error && !data) return (
    <div>
      <div style={{ color: 'var(--red)', marginBottom: 16 }}>{error}</div>
      <button style={s.emptyBtn} onClick={loadDashboard}>Retry</button>
    </div>
  );
  if (!data) return null;

  const { totals, builds, call_log } = data;
  const stripeConfigured = stripeStatus?.reason !== 'stripe_not_configured';

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={s.h1}>Creator Dashboard</h1>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{session.email}</div>
        </div>
        <button style={{ padding: '7px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontSize: 12, cursor: 'pointer' }}
                onClick={loadDashboard}>{'\u21BB'} Refresh</button>
      </div>

      {/* Empty state — no builds yet */}
      {builds.length === 0 && (
        <div style={s.emptyState}>
          <div style={s.emptyTitle}>No builds yet</div>
          <div>Publish your first build to start earning.</div>
          <button style={s.emptyBtn} onClick={() => navigate('register')}>Publish a build</button>
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--muted)' }}>
            Need help? <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate('builder-farm')}>Try the Builder Assistant</span> — it generates an AI prompt that guides you through the process.
          </div>
        </div>
      )}

      {builds.length > 0 && (
        <>
          {/* Stripe status banner */}
          {!stripeConfigured ? (
            <div style={s.stripeBox('warn')}>
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 14 }}>Stripe not configured</div>
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                Earnings are tracked. Payouts will be enabled once Stripe is set up on the server.
              </div>
            </div>
          ) : stripeStatus?.payouts_enabled ? (
            <div style={s.stripeBox('ok')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
                <span style={{ fontWeight: 500 }}>Stripe payouts enabled</span>
              </div>
            </div>
          ) : (
            <div style={s.stripeBox('default')}>
              <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Connect Stripe to receive payouts</div>
              <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 14 }}>
                {stripeStatus?.connected ? 'Complete your Stripe setup.' : 'Connect your bank account to receive 70% of call earnings.'}
              </div>
              <button style={s.stripeBtn} onClick={connectStripe}>
                {stripeStatus?.connected ? 'Complete Stripe Setup \u2192' : 'Connect Stripe \u2192'}
              </button>
            </div>
          )}

          {/* Summary cards */}
          <div style={s.grid}>
            {[
              { label: 'This month',    val: `$${totals.this_month_earnings.toFixed(2)}`, sub: `${totals.this_month_calls} calls` },
              { label: 'Pending payout', val: `$${totals.pending_earnings.toFixed(2)}`,   sub: 'not yet billed' },
              { label: 'All-time earned', val: `$${totals.total_earned.toFixed(2)}`,      sub: `${totals.total_calls} total calls` },
              { label: 'Active builds',  val: builds.filter(b => b.is_live).length,       sub: `of ${builds.length} total` },
            ].map(item => (
              <div key={item.label} style={s.card}>
                <div style={s.cardLabel}>{item.label}</div>
                <div style={s.cardVal}>{item.val}</div>
                <div style={s.cardSub}>{item.sub}</div>
              </div>
            ))}
          </div>

          {/* Builds */}
          <div style={s.section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={s.sectionTitle}>Your Builds</div>
              <button style={s.emptyBtn} onClick={() => navigate('register')}>+ Publish build</button>
            </div>
            {builds.map(b => (
              <div key={b.build_id} style={s.buildCard}>
                <div style={s.buildRow}>
                  <div>
                    <div style={s.buildName}>{b.build_name}</div>
                    <div style={s.buildSlug}>/v1/call/{b.slug}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <StatusDot isLive={b.is_live} uptime={b.uptime_pct} />
                    <button style={s.toggleBtn(b.is_live)} disabled={toggling[b.slug]}
                            onClick={() => toggleBuild(b.slug)}>
                      {toggling[b.slug] ? '...' : b.is_live ? 'Take Offline' : 'Go Live'}
                    </button>
                  </div>
                </div>
                <div style={s.buildStats}>
                  {[
                    { val: `$${b.price_per_call.toFixed(2)}`,       label: 'base price' },
                    { val: b.this_month_calls,                       label: 'calls this month' },
                    { val: `$${b.this_month_earnings.toFixed(2)}`,   label: 'earned this month' },
                    { val: `$${b.pending_earnings.toFixed(2)}`,      label: 'pending payout' },
                    { val: `${b.avg_duration_ms}ms`,                 label: 'avg duration' },
                    { val: `${b.uptime_pct}%`,                       label: 'uptime' },
                  ].map(item => (
                    <div key={item.label} style={s.stat}>
                      <div style={s.statVal}>{item.val}</div>
                      <div style={s.statLabel}>{item.label}</div>
                    </div>
                  ))}
                </div>

                {/* Library + Scan buttons */}
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    style={{ padding: '7px 16px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, border: '1px solid var(--border)', background: toolsPanel === b.slug ? 'rgba(124,111,239,0.08)' : 'transparent', color: toolsPanel === b.slug ? 'var(--accent2)' : 'var(--muted)', cursor: 'pointer' }}
                    onClick={() => openToolsPanel(b.slug)}>
                    {toolsPanel === b.slug ? 'Hide Library' : `Manage Library${buildTools[b.slug]?.length ? ` (${buildTools[b.slug].length})` : ''}`}
                  </button>
                  <button
                    style={{ padding: '7px 16px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, border: '1px solid var(--border)', background: jobTypesPanel === b.slug ? 'rgba(124,111,239,0.08)' : 'transparent', color: jobTypesPanel === b.slug ? 'var(--accent2)' : 'var(--muted)', cursor: 'pointer' }}
                    onClick={() => openJobTypesPanel(b.slug)}>
                    {jobTypesPanel === b.slug ? 'Hide Jobs' : `Manage Jobs${buildJobTypes[b.slug]?.length ? ` (${buildJobTypes[b.slug].length})` : ''}`}
                  </button>
                  <button
                    style={{ padding: '7px 16px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, border: `1px solid ${b.accepts_files ? 'rgba(34,197,94,0.4)' : 'var(--border)'}`, background: 'transparent', color: b.accepts_files ? 'var(--green)' : 'var(--muted)', cursor: 'pointer' }}
                    onClick={async () => {
                      const current = !!b.accepts_files;
                      const res = await authFetch(`/v1/builder/builds/${b.slug}`, {
                        method: 'PATCH',
                        body: JSON.stringify({
                          acceptsFiles: current ? 0 : 1,
                          ...(!current ? { acceptedFileTypes: 'image/*,application/pdf,audio/*,video/*', maxFileSize: 10485760, fileInstructions: 'Upload files alongside JSON params. Files are passed directly to the endpoint as multipart/form-data.' } : {})
                        }),
                      });
                      if (res.ok) loadDashboard();
                    }}>
                    {b.accepts_files ? 'Files: ON' : 'Enable Files'}
                  </button>
                  <button
                    style={{ padding: '7px 16px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, border: `1px solid ${scanResults[b.slug]?.passed === false ? 'rgba(239,68,68,0.4)' : scanResults[b.slug]?.passed ? 'rgba(34,197,94,0.4)' : 'var(--border)'}`, background: scanning[b.slug] ? 'rgba(124,111,239,0.08)' : 'transparent', color: scanResults[b.slug]?.passed === false ? 'var(--red)' : scanResults[b.slug]?.passed ? 'var(--green)' : 'var(--muted)', cursor: 'pointer' }}
                    disabled={scanning[b.slug]}
                    onClick={() => runScan(b.slug)}>
                    {scanning[b.slug] ? 'Scanning...' : scanResults[b.slug] ? `Score: ${scanResults[b.slug].score}/100 — Rescan` : 'Run Security Scan'}
                  </button>
                  <button
                    style={{ padding: '7px 16px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, border: `1px solid ${analyticsPanel === b.slug ? 'var(--accent)' : 'var(--border)'}`, background: analyticsPanel === b.slug ? 'rgba(124,111,239,0.08)' : 'transparent', color: analyticsPanel === b.slug ? 'var(--accent)' : 'var(--muted)', cursor: 'pointer' }}
                    onClick={() => loadAnalytics(b.slug)}>
                    Analytics
                  </button>
                  <button
                    style={{ padding: '7px 16px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, border: `1px solid ${verifyPanel === b.slug ? 'rgba(34,197,94,0.5)' : verifyData[b.slug]?.verification_tier > 0 ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`, background: verifyPanel === b.slug ? 'rgba(34,197,94,0.08)' : 'transparent', color: verifyPanel === b.slug ? 'var(--green)' : verifyData[b.slug]?.verification_tier > 0 ? 'var(--green)' : 'var(--muted)', cursor: 'pointer' }}
                    onClick={() => loadVerification(b.slug)}>
                    {verifyData[b.slug]?.verification_tier > 0 ? `Tier ${verifyData[b.slug].verification_tier} Verified` : 'Verify Build'}
                  </button>
                </div>

                {/* Analytics panel */}
                {analyticsPanel === b.slug && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                    {analyticsLoading[b.slug] ? (
                      <div style={{ color: 'var(--muted)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Loading analytics...</div>
                    ) : analyticsData[b.slug] ? (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Last 30 Days</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
                          {[
                            { label: 'Total Calls', val: analyticsData[b.slug].overview?.total_calls || 0 },
                            { label: 'Revenue', val: `$${(analyticsData[b.slug].overview?.total_revenue || 0).toFixed(2)}` },
                            { label: 'Avg Response', val: `${analyticsData[b.slug].overview?.avg_response_ms || 0}ms` },
                            { label: 'Error Rate', val: `${(analyticsData[b.slug].overview?.error_rate || 0).toFixed(1)}%` },
                            { label: 'Unique Users', val: analyticsData[b.slug].overview?.unique_consumers || 0 },
                          ].map(item => (
                            <div key={item.label} style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-xs)', padding: '10px 12px' }}>
                              <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>{item.label}</div>
                              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--accent2)' }}>{item.val}</div>
                            </div>
                          ))}
                        </div>
                        {analyticsData[b.slug].daily?.length > 0 && (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 8 }}>Daily Calls</div>
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 60, marginBottom: 4 }}>
                              {analyticsData[b.slug].daily.slice(-14).map((d, i) => {
                                const max = Math.max(...analyticsData[b.slug].daily.slice(-14).map(x => x.calls || 0), 1);
                                return (
                                  <div key={i} title={`${d.date}: ${d.calls} calls`} style={{
                                    flex: 1, height: `${((d.calls || 0) / max) * 100}%`, minHeight: 2,
                                    background: 'var(--accent)', borderRadius: '2px 2px 0 0', opacity: 0.7,
                                  }} />
                                );
                              })}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--muted)' }}>
                              <span>{analyticsData[b.slug].daily.slice(-14)[0]?.date}</span>
                              <span>{analyticsData[b.slug].daily.slice(-1)[0]?.date}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ color: 'var(--muted)', fontSize: 13 }}>No analytics data available yet.</div>
                    )}
                  </div>
                )}

                {/* Verification panel */}
                {verifyPanel === b.slug && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                    {verifyLoading[b.slug] ? (
                      <div style={{ color: 'var(--muted)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Loading verification data...</div>
                    ) : verifyData[b.slug] ? (() => {
                      const v = verifyData[b.slug];
                      const tierColors = ['var(--muted)', '#eab308', 'var(--accent)', 'var(--green)'];
                      const tierLabels = ['Unverified', 'Auto-Verified', 'Declared & Reviewed', 'Track Record'];
                      const payoutPcts = [70, 73, 77, 80];
                      const currentTier = v.verification_tier || 0;
                      return (
                        <div>
                          {/* Current tier + payout banner */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderRadius: 'var(--radius-sm)', background: `${tierColors[currentTier]}11`, border: `1px solid ${tierColors[currentTier]}44`, marginBottom: 16 }}>
                            <div>
                              <div style={{ fontSize: 16, fontWeight: 700, color: tierColors[currentTier] }}>
                                Tier {currentTier}: {tierLabels[currentTier]}
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                                Higher tiers unlock better payout rates
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: 24, fontWeight: 700, color: tierColors[currentTier] }}>
                                {payoutPcts[currentTier]}%
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--muted)' }}>payout rate</div>
                            </div>
                          </div>

                          {/* Tier progression bar */}
                          <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
                            {[0, 1, 2, 3].map(tier => (
                              <div key={tier} style={{ flex: 1, textAlign: 'center' }}>
                                <div style={{ height: 6, borderRadius: 3, background: tier <= currentTier ? tierColors[tier] : 'var(--border)', marginBottom: 6, transition: 'background 0.3s' }} />
                                <div style={{ fontSize: 10, color: tier <= currentTier ? tierColors[tier] : 'var(--muted)', fontWeight: tier === currentTier ? 700 : 400 }}>
                                  {payoutPcts[tier]}%
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Tier 1: Auto-checks */}
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: v.tier1?.passed ? 'rgba(34,197,94,0.15)' : 'rgba(124,111,239,0.1)', color: v.tier1?.passed ? 'var(--green)' : 'var(--muted)' }}>1</span>
                              Auto-Verified (Scanner)
                              {v.tier1?.passed && <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 500 }}>Passed</span>}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6 }}>
                              {[
                                { key: 'https', label: 'HTTPS endpoint' },
                                { key: 'health_endpoint', label: 'Health check' },
                                { key: 'error_codes', label: 'Error codes' },
                                { key: 'tool_docs', label: 'Tool docs' },
                                { key: 'response_time', label: 'Fast response (<2s)' },
                              ].map(check => {
                                const ok = v.tier1?.checks?.[check.key];
                                return (
                                <div key={check.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 'var(--radius-xs)', background: 'var(--bg)', fontSize: 12 }}>
                                  <span style={{ width: 16, height: 16, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, background: ok ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)', color: ok ? 'var(--green)' : 'var(--red)' }}>
                                    {ok ? '✓' : '✗'}
                                  </span>
                                  <span style={{ color: ok ? 'var(--text)' : 'var(--muted)' }}>{check.label}</span>
                                </div>
                                );
                              })}
                            </div>
                            {!v.tier1?.passed && (
                              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
                                Run a Security Scan above to auto-check these. All 5 must pass for Tier 1.
                              </div>
                            )}
                          </div>

                          {/* Tier 2: Builder declarations */}
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: v.tier2?.verified ? 'rgba(124,111,239,0.15)' : 'rgba(124,111,239,0.1)', color: v.tier2?.verified ? 'var(--accent)' : 'var(--muted)' }}>2</span>
                              Declarations (Builder + Admin Review)
                              {v.tier2?.verified ? (
                                <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 500 }}>Verified</span>
                              ) : v.tier2?.declared ? (
                                <span style={{ fontSize: 11, color: '#eab308', fontWeight: 500 }}>Pending Review</span>
                              ) : null}
                            </div>
                            {!v.tier1?.passed && (
                              <div style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 14px', background: 'var(--bg)', borderRadius: 'var(--radius-xs)' }}>
                                Complete Tier 1 first (pass all auto-checks) before declaring Tier 2.
                              </div>
                            )}
                            {v.tier1?.passed && !v.tier2?.verified && (
                              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 16 }}>
                                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
                                  Declare what your build uses. Our team will review and verify these claims.
                                  Verified builds earn <strong style={{ color: 'var(--accent2)' }}>77% payout</strong> (up from 73%).
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                  <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Models Used</label>
                                    <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                      value={verifyForm.modelsUsed}
                                      onChange={e => setVerifyForm(f => ({ ...f, modelsUsed: e.target.value }))}
                                      placeholder="e.g. GPT-4, Claude 3.5 Sonnet, custom LSTM" />
                                  </div>
                                  <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Data Sources</label>
                                    <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                      value={verifyForm.dataSources}
                                      onChange={e => setVerifyForm(f => ({ ...f, dataSources: e.target.value }))}
                                      placeholder="e.g. Binance API, CoinGecko, proprietary database" />
                                  </div>
                                  <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Data Freshness</label>
                                    <select style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                      value={verifyForm.dataFreshness}
                                      onChange={e => setVerifyForm(f => ({ ...f, dataFreshness: e.target.value }))}>
                                      <option value="real-time">Real-time</option>
                                      <option value="hourly">Hourly</option>
                                      <option value="daily">Daily</option>
                                      <option value="weekly">Weekly</option>
                                      <option value="static">Static</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Privacy Stance</label>
                                    <select style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                      value={verifyForm.privacyStance}
                                      onChange={e => setVerifyForm(f => ({ ...f, privacyStance: e.target.value }))}>
                                      <option value="no_logging">No Logging</option>
                                      <option value="anonymized">Anonymized Logs</option>
                                      <option value="logs_requests">Logs Requests</option>
                                    </select>
                                  </div>
                                  <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Server Region</label>
                                    <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                      value={verifyForm.serverRegion}
                                      onChange={e => setVerifyForm(f => ({ ...f, serverRegion: e.target.value }))}
                                      placeholder="e.g. EU (Frankfurt), US-East" />
                                  </div>
                                </div>
                                <button onClick={() => submitVerification(b.slug)} disabled={verifySubmitting}
                                  style={{ marginTop: 14, padding: '9px 22px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                  {verifySubmitting ? 'Submitting...' : v.tier2?.declared ? 'Update Declaration' : 'Submit for Review'}
                                </button>
                              </div>
                            )}
                            {v.tier2?.verified && (
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                {[
                                  { label: 'Models', val: v.tier2.models_used },
                                  { label: 'Data Sources', val: v.tier2.data_sources },
                                  { label: 'Freshness', val: v.tier2.data_freshness },
                                  { label: 'Privacy', val: v.tier2.privacy_stance?.replace(/_/g, ' ') },
                                  { label: 'Region', val: v.tier2.server_region },
                                ].filter(x => x.val).map(item => (
                                  <div key={item.label} style={{ padding: '8px 12px', background: 'var(--bg)', borderRadius: 'var(--radius-xs)', fontSize: 12 }}>
                                    <div style={{ color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', fontWeight: 600, marginBottom: 2 }}>{item.label}</div>
                                    <div style={{ color: 'var(--text)', fontWeight: 500 }}>{item.val}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Tier 3: Track record */}
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: v.tier3?.passed ? 'rgba(34,197,94,0.15)' : 'rgba(124,111,239,0.1)', color: v.tier3?.passed ? 'var(--green)' : 'var(--muted)' }}>3</span>
                              Track Record (Auto-Earned)
                              {v.tier3?.passed && <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 500 }}>Earned</span>}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
                              Automatically earned when your build has strong metrics: 95%+ uptime, 50+ calls, 0 open disputes, 4.0+ rating.
                              Unlocks <strong style={{ color: 'var(--accent2)' }}>80% payout</strong>.
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                              {[
                                { label: '30d Uptime', val: `${(v.tier3?.metrics?.uptime_30d || 0).toFixed(1)}%`, ok: (v.tier3?.metrics?.uptime_30d || 0) >= 95 },
                                { label: 'Total Calls', val: v.tier3?.metrics?.calls_total || 0, ok: (v.tier3?.metrics?.calls_total || 0) >= 50 },
                                { label: 'Open Disputes', val: v.tier3?.metrics?.disputes_open || 0, ok: (v.tier3?.metrics?.disputes_open || 0) === 0 },
                                { label: 'Avg Rating', val: (v.tier3?.metrics?.avg_rating || 0).toFixed(1), ok: (v.tier3?.metrics?.avg_rating || 0) >= 4.0 || (v.tier3?.metrics?.calls_total || 0) === 0 },
                              ].map(item => (
                                <div key={item.label} style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 'var(--radius-xs)', border: `1px solid ${item.ok ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.15)'}` }}>
                                  <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>{item.label}</div>
                                  <div style={{ fontSize: 16, fontWeight: 600, color: item.ok ? 'var(--green)' : 'var(--red)' }}>{item.val}</div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Model Integrity Monitor */}
                          {v.model_integrity && v.tier2?.verified && (
                            <div style={{ marginTop: 16 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: v.model_integrity.mismatches > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)', color: v.model_integrity.mismatches > 0 ? 'var(--red)' : 'var(--green)' }}>⚡</span>
                                Model Integrity
                                {v.model_integrity.mismatches === 0 && <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 500 }}>Clean</span>}
                                {v.model_integrity.mismatches > 0 && <span style={{ fontSize: 11, color: 'var(--red)', fontWeight: 500 }}>{v.model_integrity.mismatches} mismatch{v.model_integrity.mismatches !== 1 ? 'es' : ''}</span>}
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 8 }}>
                                Every API call checks your response for model identification (<code style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent2)' }}>x-model</code> header or <code style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent2)' }}>model</code> field).
                                {v.model_integrity.mismatches >= 5 && <><br /><strong style={{ color: 'var(--red)' }}>Warning:</strong> {v.model_integrity.threshold - v.model_integrity.mismatches} mismatches remaining before Tier 2 auto-revocation.</>}
                              </div>
                              {v.model_integrity.mismatches > 0 && v.model_integrity.last_mismatch_model && (
                                <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 'var(--radius-xs)', fontSize: 12 }}>
                                  <span style={{ color: 'var(--muted)' }}>Last detected: </span>
                                  <strong style={{ color: 'var(--red)' }}>{v.model_integrity.last_mismatch_model}</strong>
                                  <span style={{ color: 'var(--muted)' }}> (declared: {v.tier2?.models_used})</span>
                                </div>
                              )}
                              {v.tier2?.revoked_reason === 'model_mismatch_auto' && (
                                <div style={{ marginTop: 8, padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--red)', fontWeight: 500 }}>
                                  Tier 2 was auto-revoked due to persistent model mismatches. Update your declared models or ensure your endpoint consistently reports the correct model.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })() : (
                      <div style={{ color: 'var(--muted)', fontSize: 13 }}>Could not load verification data.</div>
                    )}
                  </div>
                )}

                {/* Scan results panel */}
                {scanResults[b.slug] && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, background: scanResults[b.slug].passed ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: scanResults[b.slug].passed ? 'var(--green)' : 'var(--red)' }}>
                        {scanResults[b.slug].score}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                          {scanResults[b.slug].passed ? 'Ready to go live' : 'Issues found'}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{scanResults[b.slug].summary}</div>
                      </div>
                    </div>
                    {!scanResults[b.slug].passed && (
                      <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--muted)' }}>
                        Need help fixing these? <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate('builder-farm')}>Get a tailored AI prompt</span> for this build.
                      </div>
                    )}

                    {/* Issues */}
                    {scanResults[b.slug].issues.length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        {scanResults[b.slug].issues.map((issue, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, padding: '8px 12px', marginBottom: 4, borderRadius: 'var(--radius-sm)', background: issue.severity === 'critical' ? 'rgba(239,68,68,0.06)' : issue.severity === 'warning' ? 'rgba(234,179,8,0.06)' : 'rgba(124,111,239,0.04)', border: `1px solid ${issue.severity === 'critical' ? 'rgba(239,68,68,0.2)' : issue.severity === 'warning' ? 'rgba(234,179,8,0.2)' : 'rgba(124,111,239,0.1)'}` }}>
                            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: issue.severity === 'critical' ? 'var(--red)' : issue.severity === 'warning' ? '#eab308' : 'var(--accent2)', minWidth: 60 }}>
                              {issue.severity}
                            </span>
                            <span style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>{issue.message}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Model verification */}
                    {scanResults[b.slug].modelVerification && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                          Model Verification
                        </div>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                          borderRadius: 'var(--radius-sm)', background: 'var(--bg)',
                          border: `1px solid ${
                            scanResults[b.slug].modelVerification.status === 'verified' ? 'rgba(34,197,94,0.3)' :
                            scanResults[b.slug].modelVerification.status === 'mismatch' ? 'rgba(239,68,68,0.3)' :
                            'var(--border)'
                          }`,
                        }}>
                          <span style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 600,
                            background: scanResults[b.slug].modelVerification.status === 'verified' ? 'rgba(34,197,94,0.12)' :
                                        scanResults[b.slug].modelVerification.status === 'mismatch' ? 'rgba(239,68,68,0.12)' :
                                        'rgba(234,179,8,0.12)',
                            color: scanResults[b.slug].modelVerification.status === 'verified' ? 'var(--green)' :
                                   scanResults[b.slug].modelVerification.status === 'mismatch' ? 'var(--red)' :
                                   '#eab308',
                          }}>
                            {scanResults[b.slug].modelVerification.status === 'verified' ? 'Verified' :
                             scanResults[b.slug].modelVerification.status === 'mismatch' ? 'Mismatch' : 'Unverified'}
                          </span>
                          <div style={{ fontSize: 13 }}>
                            <span style={{ color: 'var(--muted)' }}>Claims: </span>
                            <strong>{scanResults[b.slug].modelVerification.claimed}</strong>
                            {scanResults[b.slug].modelVerification.detected && (
                              <span style={{ color: 'var(--muted)' }}> — Detected: <strong style={{ color: 'var(--text)' }}>{scanResults[b.slug].modelVerification.detected}</strong></span>
                            )}
                          </div>
                        </div>
                        {scanResults[b.slug].modelVerification.status === 'unverified' && (
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                            Add a <code style={{ fontFamily: 'monospace', color: 'var(--accent2)' }}>model</code> field to your JSON response or an <code style={{ fontFamily: 'monospace', color: 'var(--accent2)' }}>x-model</code> header to get a verified badge.
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tool results */}
                    {scanResults[b.slug].tools?.results?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                          Tool Validation ({scanResults[b.slug].tools.passed}/{scanResults[b.slug].tools.total} passed)
                        </div>
                        {scanResults[b.slug].tools.results.map((t, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', marginBottom: 3, borderRadius: 'var(--radius-sm)', background: 'var(--bg)' }}>
                            <div style={{ fontSize: 13 }}>
                              <span style={{ fontWeight: 600 }}>{t.name}</span>
                              <span style={{ color: 'var(--muted)', fontSize: 11, marginLeft: 8 }}>{t.slug}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ fontSize: 11, color: 'var(--muted)' }}>{t.ms}ms</span>
                              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 600, background: t.passed ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: t.passed ? 'var(--green)' : 'var(--red)' }}>
                                {t.passed ? `${t.status} OK` : t.error || `HTTP ${t.status}`}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Tools panel */}
                {toolsPanel === b.slug && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
                      Tools / Commands Library
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 }}>
                      Define available tools with individual pricing. Consumers see this as your build's library.
                      Each tool can have a different price — cheap lookups vs expensive analysis.
                    </div>

                    {/* Existing tools */}
                    {(buildTools[b.slug] || []).map(tool => (
                      <div key={tool.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 8 }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{tool.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'monospace' }}>{tool.slug}</div>
                          {tool.description && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{tool.description}</div>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 99, background: 'rgba(124,111,239,0.1)', color: 'var(--accent2)', fontWeight: 600 }}>{tool.category}</span>
                          <span style={{ fontWeight: 700, color: 'var(--accent2)', fontSize: 15 }}>${tool.price_per_call.toFixed(2)}</span>
                          <button onClick={() => deleteTool(b.slug, tool.slug)} style={{ padding: '4px 10px', fontSize: 11, border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--red)', cursor: 'pointer' }}>Delete</button>
                        </div>
                      </div>
                    ))}

                    {/* Add new tool form */}
                    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 16, marginTop: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Add tool</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Name *</label>
                          <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                            value={newTool.name} onChange={e => setNewTool(t => ({ ...t, name: e.target.value, slug: autoToolSlug(e.target.value) }))}
                            placeholder="List Symbols" />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Slug *</label>
                          <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', fontFamily: 'monospace', boxSizing: 'border-box' }}
                            value={newTool.slug} onChange={e => setNewTool(t => ({ ...t, slug: e.target.value }))}
                            placeholder="list-symbols" />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Description</label>
                          <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                            value={newTool.description} onChange={e => setNewTool(t => ({ ...t, description: e.target.value }))}
                            placeholder="Returns all available trading symbols" />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Example command (JSON payload)</label>
                          <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', fontFamily: 'monospace', boxSizing: 'border-box' }}
                            value={newTool.command} onChange={e => setNewTool(t => ({ ...t, command: e.target.value }))}
                            placeholder='{"type": "symbols"}' />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Price per call ($)</label>
                          <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                            type="number" min="0" step="0.01" value={newTool.pricePerCall}
                            onChange={e => setNewTool(t => ({ ...t, pricePerCall: e.target.value }))} />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Category</label>
                          <select style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                            value={newTool.category} onChange={e => setNewTool(t => ({ ...t, category: e.target.value }))}>
                            <option value="default">Default</option>
                            <option value="query">Query</option>
                            <option value="analysis">Analysis</option>
                            <option value="data">Data</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      </div>
                      {toolError && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{toolError}</div>}
                      <button onClick={() => addTool(b.slug)} disabled={toolSaving}
                        style={{ marginTop: 12, padding: '8px 20px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                        {toolSaving ? 'Adding...' : 'Add Tool'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Job Types Panel */}
                {jobTypesPanel === b.slug && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Job Types</div>
                    {(buildJobTypes[b.slug] || []).map(jt => (
                      <div key={jt.slug} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xs)', marginBottom: 8 }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{jt.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'monospace' }}>{jt.slug}</div>
                          {jt.description && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{jt.description}</div>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontWeight: 600, color: 'var(--accent2)' }}>${jt.price.toFixed(2)}</span>
                          <button onClick={() => deleteJobType(b.slug, jt.slug)}
                            style={{ padding: '3px 10px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 'var(--radius-xs)', background: 'transparent', color: 'var(--red)', cursor: 'pointer' }}>Delete</button>
                        </div>
                      </div>
                    ))}

                    {/* Add job type form */}
                    <div style={{ marginTop: 14, padding: 16, background: 'var(--bg3)', borderRadius: 'var(--radius-sm)' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 10 }}>Add Job Type</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Name</div>
                          <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} value={newJobType.name}
                            onChange={e => setNewJobType(f => ({ ...f, name: e.target.value, slug: autoToolSlug(e.target.value) }))}
                            placeholder="Monte Carlo Simulation" />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Slug</div>
                          <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} value={newJobType.slug}
                            onChange={e => setNewJobType(f => ({ ...f, slug: e.target.value }))}
                            placeholder="monte-carlo" />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Description</div>
                          <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} value={newJobType.description}
                            onChange={e => setNewJobType(f => ({ ...f, description: e.target.value }))}
                            placeholder="Run N price path simulations..." />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Price ($)</div>
                          <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} value={newJobType.price} type="number" step="0.10" min="0.10"
                            onChange={e => setNewJobType(f => ({ ...f, price: e.target.value }))} />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Timeout (ms)</div>
                          <input style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} value={newJobType.timeoutMs} type="number" step="60000" min="60000" max="600000"
                            onChange={e => setNewJobType(f => ({ ...f, timeoutMs: e.target.value }))} />
                        </div>
                      </div>
                      {jobTypeError && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{jobTypeError}</div>}
                      <button onClick={() => addJobType(b.slug)} disabled={jobTypeSaving}
                        style={{ marginTop: 12, padding: '8px 20px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                        {jobTypeSaving ? 'Adding...' : 'Add Job Type'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Call log */}
          <div style={s.section}>
            <div style={s.sectionTitle}>Recent calls</div>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>{['Time', 'Build', 'Status', 'Duration', 'Your earnings', 'Billed'].map(h =>
                    <th key={h} style={s.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {call_log.length === 0 ? (
                    <tr><td colSpan={6} style={{ ...s.td, color: 'var(--muted)', textAlign: 'center', padding: 32 }}>No calls yet.</td></tr>
                  ) : call_log.map((c, i) => {
                    const ok = c.status >= 200 && c.status < 300;
                    const earned = ok ? (c.price_per_call * 0.70).toFixed(3) : '0.000';
                    return (
                      <tr key={i}>
                        <td style={{ ...s.td, color: 'var(--muted)', fontSize: 12 }}>{new Date(c.called_at).toLocaleTimeString()}</td>
                        <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>{c.slug}</td>
                        <td style={s.td}><span style={s.badge(ok)}>{c.status || 'ERR'}</span></td>
                        <td style={{ ...s.td, color: 'var(--muted)' }}>{c.duration_ms ? `${c.duration_ms}ms` : '\u2014'}</td>
                        <td style={{ ...s.td, color: ok ? 'var(--accent2)' : 'var(--muted)' }}>${earned}</td>
                        <td style={{ ...s.td, color: 'var(--muted)', fontSize: 12 }}>{c.billed ? '\u2713 billed' : 'pending'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
